import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getImagePath } from '../utils/imagePath';

const Election2026 = () => {
  const [activeTab, setActiveTab] = useState('timetable');
  const [committeeSubTab, setCommitteeSubTab] = useState('secretariat');
  const [scrolled, setScrolled] = useState(false);
  const [showCandidatesModal, setShowCandidatesModal] = useState(false);
  
  // Flip popup state
  const [showFlipPopup, setShowFlipPopup] = useState(true);
  const [isFlipped, setIsFlipped] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-show popup
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowFlipPopup(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const timetable = [
    { date: '18 May', activity: 'Announcement of Electoral Committee (ELCO) and Elections Timetable & Guidelines', highlight: false },
    { date: '22 May', activity: 'Opening of Nominations', highlight: true },
    { date: '5 Jun', activity: 'Close of Nominations', highlight: false },
    { date: '6 -8 Jun', activity: 'Screening & Verification', highlight: false },
    { date: '9 Jun', activity: 'Publication of Initial Candidates\' List', highlight: false },
    { date: '9 – 10 Jun', activity: 'Petitioning', highlight: false },
    { date: '11 Jun', activity: 'Publication of Final Candidates\' List', highlight: false },
    { date: '11- 14 Jun', activity: 'Campaign Period', highlight: true },
    { date: '15 Jun', activity: 'Candidates Forum / Townhall', highlight: false },
    { date: '16 Jun', activity: 'Final Election Notice', highlight: false },
    { date: '16 Jun', activity: 'Election Day', highlight: true },
    { date: '16 Jun', activity: 'Announcement of Results', highlight: false },
    { date: 'First week of July', activity: 'Transition & Handover Support', highlight: false }
  ];

  const elcoMembers = {
    chairman: {
      name: 'Samson Adeosun',
      role: 'Chairman',
      email: 'samson.adeosun@nlng.com',
      description: 'Leading the 2026 Electoral Committee and accountable for the integrity, fairness and timely conduct of the entire election cycle.',
      initials: 'SA',
      image: 'SA_RA.jpg'
    },
    secretariat: [
      { name: 'Abdulmalik Ibrahim', role: 'Secretary', email: 'abdulmalik.ibrahim@nlng.com', initials: 'AI' },
      { name: 'Mariam Ezekwesili', role: 'Assistant Secretary', email: 'mariam.ezekwesili@nlng.com', initials: 'ME' },
      { name: 'Lorraine Ichoku', role: 'Secretariat', email: 'lorraine.ichoku@nlng.com', initials: 'LI' },
      { name: 'Emmanuel Nwachukwu', role: 'Secretariat', email: 'emmanuel.nwachukwu@nlng.com', initials: 'EN' }
    ],
    committee: [
      { name: 'Kenneth Ezugwu', role: 'Committee Member', email: 'kenneth.ezugwu@nlng.com', initials: 'KE' },
      { name: 'Ikenna Ikonta', role: 'Committee Member', email: 'ikenna.ikonta@nlng.com', initials: 'II' },
      { name: 'Oluwafolakemi Olumuyiwa', role: 'Committee Member', email: 'folakemi.olumuyiwa@nlng.com', initials: 'OO' },
      { name: 'Mrs. Victoria Obakpolor', role: 'Committee Member', email: 'obakpolor@nlng.com', initials: 'OB' }
    ],
    publication: [
      { name: 'Oyegbami Oludare', role: 'Publication', email: 'oyegbami.oludare@nlng.com', initials: 'OO' },
      { name: 'Louis Osakwe', role: 'Publication', email: 'louis.osakwe@nlng.com', initials: 'LO' },
      { name: 'Eno-Obong Ukpong', role: 'Publication', email: 'eno.ukpong@nlng.com', initials: 'EU' },
      { name: 'Farouk Shagari', role: 'Publication', email: 'farouk.shagari@nlng.com', initials: 'FS' }
    ]
  };

  const guidelines = [
    {
      num: 1,
      title: 'Who can contest?',
      content: [
        'All Full Members may contest, except where Direct Staff / Secondee restrictions apply'
      ]
    },
    {
      num: 2,
      title: 'Who can vote?',
      content: [
        '• All Full Members - NLNG / NSML / RAIS Direct Staff and Secondees based on Bonny',
        '• Registered spouses of Full Members who are RA residents',
        '• Associate Members do not vote and cannot contest'
      ]
    },
    {
      num: 3,
      title: 'How will voting happen?',
      content: [
        '• Voting will be conducted electronically via an authenticated, auditable online platform.',
        '• Login credentials and the voting window will be communicated by ELCO closer to election day.'
      ]
    },
    {
      num: 4,
      title: 'Restrictions to note',
      content: [
        '• Two consecutive terms in the same position = not eligible to contest that position again',
        '• ELCO members and their immediate families cannot contest',
        '• HRE/2 unit members cannot contest',
        '• One candidate, one position only',
        '• All winners are decided by simple majority. Ties trigger a re-vote.'
      ]
    },
    {
      num: 5,
      title: 'Conflict of Interest Declaration',
      content: [
        '• Per the 2026 AGM amendment, every elected RCEC member must sign a Conflict of Interest Declaration and Code of Conduct Acknowledgement Form within 14 days of assumption of office.'
      ]
    },
    {
      num: 6,
      title: 'Positions available',
      content: [
        'Eight (8) positions open for contest:',
        '• Vice President',
        '• General Secretary',
        '• Assistant General Secretary',
        '• Director of Socials',
        '• Director of Sports & Wellness',
        '• Director of Culture',
        '• Director of Technicals',
        '• Director, International Community'
      ]
    }
  ];

  const handleViewPDF = (fileType) => {
    let filePath = '';
    let fileName = '';
    
    if (fileType === 'guidelines') {
      filePath = '/2026_Election_Guidelines.pdf';
      fileName = '2026_Election_Guidelines.pdf';
    } else if (fileType === 'constitution') {
      filePath = '/RA_Club_Constitution_2026.pdf';
      fileName = 'RA_Club_Constitution_2026.pdf';
    } else {
      alert('Unknown file type requested');
      return;
    }
    
    window.open(filePath, '_blank');
  };

  // Election results data from the image
  const electionResults = [
    { position: 'Vice President', elected: 'No Submission Received' },
    { position: 'General Secretary', elected: 'Kayode Owaboye' },
    { position: 'Assistant General Secretary', elected: 'Asolia Jumbo' },
    { position: 'Director of Sports & Wellness', elected: 'Temitayo Ogundoju' },
    { position: 'Director of Socials', elected: 'Tajudeen Lasisi' },
    { position: 'Director of Culture', elected: 'Augustina Sobo' },
    { position: 'Director of Technicals', elected: 'Olujide Shoyebi' },
    { position: 'Director, Int\'l Community', elected: 'Vikash Kumar' }
  ];

  const candidatesData = [
    { position: 'Vice President', candidates: 'No submission received' },
    { position: 'General Secretary', candidates: 'Kayode Owaboye' },
    { position: 'Assistant General Secretary', candidates: 'Asolia Jumbo' },
    { position: 'Director of Sports & Wellness', candidates: 'Temitayo Ogundoju' },
    { position: 'Director of Socials', candidates: 'Victor Segun Henry; Tajudeen Lasisi; Enem Okaula' },
    { position: 'Director of Culture', candidates: 'Mackiel Ezekiel; Augustina Sobo; Ifeanyi Okoro' },
    { position: 'Director of Technicals', candidates: 'Olujide Shoyebi; Nnanyelum Okoli-Ejike; Miracle Kaka Mpio-Obari' },
    { position: 'Director, International Community', candidates: 'Vikash Kumar' }
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.2
      }
    }
  };

  const glowOnHover = {
    rest: { boxShadow: "0 4px 12px rgba(0,0,0,0.05)" },
    hover: { boxShadow: "0 20px 40px rgba(34,197,94,0.15), 0 0 0 2px rgba(34,197,94,0.2)", transition: { duration: 0.2 } }
  };

  const committeeSubTabs = [
    { id: 'secretariat', label: 'Secretariat', icon: 'fa-building', members: elcoMembers.secretariat, color: '#166534' },
    { id: 'committee', label: 'Committee', icon: 'fa-handshake', members: elcoMembers.committee, color: '#22c55e' },
    { id: 'publication', label: 'Publication', icon: 'fa-newspaper', members: elcoMembers.publication, color: '#4ade80' }
  ];

  return (
    <div className="election-page">
      <div className="bg-animation">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      {/* FLIP POPUP - Premium Design */}
      <AnimatePresence>
        {showFlipPopup && (
          <motion.div 
            className="flip-popup-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowFlipPopup(false)}
          >
            <motion.div 
              className="flip-popup-container"
              initial={{ scale: 0.85, y: 60, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.85, y: 60, opacity: 0 }}
              transition={{ type: "spring", damping: 30, stiffness: 280 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <motion.button 
                className="flip-popup-close" 
                onClick={() => setShowFlipPopup(false)}
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
              >
                <i className="fas fa-times"></i>
              </motion.button>

              {/* Flip Card */}
              <motion.div 
                className={`flip-card ${isFlipped ? 'flipped' : ''}`} 
                onClick={() => setIsFlipped(!isFlipped)}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                <motion.div 
                  className="flip-card-inner"
                  animate={{ rotateY: isFlipped ? 180 : 0 }}
                  transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
                >
                  {/* FRONT - Premium Green Card with White Text */}
                  <motion.div 
                    className="flip-card-front"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                  >
                    <div className="flip-front-content">
                      <motion.div 
                        className="flip-icon"
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
                      >
                        <i className="fas fa-check-circle"></i>
                      </motion.div>
                      <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6, duration: 0.5 }}
                      >
                        MEET YOUR NEW
                      </motion.h2>
                      <motion.h1
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.7, type: "spring", stiffness: 150 }}
                      >
                        RCEC
                      </motion.h1>
                      <motion.p 
                        className="flip-sub"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.9 }}
                        transition={{ delay: 0.8, duration: 0.5 }}
                      >
                        2026 - 2028 Executive Committee
                      </motion.p>
                      <motion.div 
                        className="flip-tap-hint"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9, duration: 0.4 }}
                        whileHover={{ scale: 1.05 }}
                      >
                        <i className="fas fa-hand-pointer"></i> Tap to flip
                      </motion.div>
                    </div>
                  </motion.div>
                  
                  {/* BACK - Professional Results with Yellowish-Orange Theme & Scroll */}
                  <motion.div 
                    className="flip-card-back"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                  >
                    <div className="flip-back-content">
                      <div className="flip-back-header">
                        <span className="flip-badge">
                          <i className="fas fa-star"></i> RESULTS DECLARED
                        </span>
                        <h3>Meet Your New RCEC</h3>
                        <p className="flip-back-sub">2026 - 2028 Executive Committee</p>
                        <p className="flip-back-note">
                          Following the collation and verification of votes, the duly elected officers 
                          of the 2026 RA Club Executive Committee are hereby declared. Congratulations.
                        </p>
                      </div>
                      <div className="flip-results-table">
                        <table>
                          <thead>
                            <tr>
                              <th>POSITION</th>
                              <th>ELECTED OFFICER</th>
                            </tr>
                          </thead>
                          <tbody>
                            {electionResults.map((item, idx) => (
                              <motion.tr 
                                key={idx} 
                                className={item.elected === 'No Submission Received' ? 'no-sub-row' : ''}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.05 + 0.3 }}
                              >
                                <td className="pos-cell">{item.position}</td>
                                <td className="elected-cell">
                                  {item.elected === 'No Submission Received' ? (
                                    <span className="no-sub-text"><i className="fas fa-clock"></i> No Submission Received</span>
                                  ) : (
                                    <span className="elected-name"><i className="fas fa-user-check"></i> {item.elected}</span>
                                  )}
                                </td>
                              </motion.tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="flip-back-footer">
                        <p>
                          <i className="fas fa-gavel"></i> Handover & Inauguration: <strong>First week of July 2026</strong>
                        </p>
                        <p className="flip-issued">
                          ISSUED BY THE RA CLUB ELECTORAL COMMITTEE · SAMSON ADEOSUN, CHAIRMAN · ABDULMALIK IBRAHIM, SECRETARY (EXT. 7216)
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              </motion.div>
              
              <div className="flip-popup-hint">
                <span>Click the card to flip between front and back</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.section 
        className="election-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="hero-overlay"></div>
        <div className="container">
          <motion.div 
            className="hero-content"
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <motion.span 
              className="hero-eyebrow"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <span className="pulse-dot"></span>
              RA Club · Election Season 2026
            </motion.span>
            <motion.h1 
              className="hero-title"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              Shape the next chapter of the <span className="text-gradient">RA Club</span>
            </motion.h1>
            <motion.p 
              className="hero-subtitle"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
            >
              The tenure of the current RCEC ends in 2026. The Electoral Committee (ELCO) 
              is now conducting elections into ten executive positions. Read the full guidelines, 
              check the timetable and pick up a nomination form right here.
            </motion.p>
            <motion.div 
              className="hero-actions"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
            >
              <motion.button 
                whileHover={{ scale: 1.05, y: -3, boxShadow: "0 10px 25px -5px rgba(234,179,8,0.5)" }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowCandidatesModal(true)}
                className="btn btn-yellow"
              >
                <i className="fas fa-users-viewfinder"></i> Candidates List
              </motion.button>

              <motion.button 
                whileHover={{ scale: 1.05, y: -3, boxShadow: "0 10px 25px -5px rgba(34,197,94,0.4)" }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleViewPDF('guidelines')}
                className="btn btn-primary"
              >
                <i className="fas fa-eye"></i> Full Guidelines
                <motion.span className="btn-glow" />
              </motion.button>
     
              <motion.button 
                whileHover={{ scale: 1.05, y: -3, borderColor: "#22c55e", color: "#4ade80", backgroundColor: "rgba(34,197,94,0.1)" }}
                whileTap={{ scale: 0.98 }}
                onClick={() => window.open('https://forms.cloud.microsoft.com/pages/responsepage.aspx?id=OQ5hJ6_hxkKyD4DKjIV5xsXTxNbd7gJOspkFWdMHjpdUNkFTQTVCREk5WE9TMjBVMUowUkNSNkRZSS4u&route=shorturl', '_blank')}
                className="btn btn-outline"
              >
                <i className="fas fa-file-alt"></i> Nomination Form
              </motion.button>

              <motion.button 
                whileHover={{ scale: 1.05, y: -3 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleViewPDF('constitution')}
                className="btn btn-secondary"
              >
                <i className="fas fa-gavel"></i> Constitution
              </motion.button>  
            </motion.div>
          </motion.div>
        </div>
        <motion.div 
          className="hero-wave"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1.2, delay: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        />
      </motion.section>

      <motion.div 
        className="info-strip"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.7 }}
      >
        <div className="container">
          <div className="info-grid">
            {[
              { label: 'Nominations Deadline', value: '5 Jun 2026', icon: 'fa-calendar-plus' },
              { label: 'Campaign Period', value: '11 - 14 Jun 2026', icon: 'fa-bullhorn' },
              { label: 'Election Day', value: '16 June 2026', icon: 'fa-check-circle' },
              { label: 'Mode of Voting', value: 'Electronic Ballot', icon: 'fa-laptop' }
            ].map((item, idx) => (
              <motion.div 
                key={idx}
                className="info-tile"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.8 + (idx * 0.1) }}
                whileHover={{ scale: 1.05, y: -8, borderColor: "#22c55e", backgroundColor: "white", boxShadow: "0 15px 30px -12px rgba(34,197,94,0.15)" }}
              >
                <motion.div className="info-icon" whileHover={{ rotate: 360, transition: { duration: 0.6 } }}>
                  <i className={`fas ${item.icon}`}></i>
                </motion.div>
                <div>
                  <span className="info-label">{item.label}</span>
                  <span className="info-value">{item.value}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      <motion.div 
        className={`election-tabs ${scrolled ? 'scrolled' : ''}`}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.9 }}
      >
        <div className="container">
          <div className="tabs-nav">
            {[
              { id: 'timetable', icon: 'fa-calendar-alt', label: 'Timetable' },
              { id: 'guidelines', icon: 'fa-gavel', label: 'Guidelines' },
              { id: 'committee', icon: 'fa-users', label: 'Committee' }
            ].map((tab) => (
              <motion.button
                key={tab.id}
                className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <i className={`fas ${tab.icon}`}></i>
                <span>{tab.label}</span>
                {activeTab === tab.id && <motion.div className="tab-indicator" layoutId="tabIndicator" />}
              </motion.button>
            ))}
          </div>
        </div>
      </motion.div>

      <section className="election-content">
        <div className="container">
          <AnimatePresence mode="wait">
            {activeTab === 'timetable' && (
              <motion.div
                key="timetable"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -40 }}
                transition={{ duration: 0.5 }}
                className="timetable-section"
              >
                <motion.div 
                  className="section-header"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <motion.div 
                    className="section-badge"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.4, type: "spring" }}
                  >
                    <i className="fas fa-calendar-alt"></i> Schedule
                  </motion.div>
                  <h2>Elections <span className="text-green">Timetable</span></h2>
                  <motion.div className="section-line" />
                  <p>Confirmed dates from ELCO. Binding on candidates, voters and both the outgoing and incoming RCEC.</p>
                </motion.div>

                <motion.div 
                  className="timetable-wrapper"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  variants={glowOnHover}
                  whileHover="hover"
                >
                  <table className="timetable">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Activity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {timetable.map((item, idx) => (
                        <motion.tr 
                          key={idx} 
                          className={item.highlight ? 'highlight-row' : ''}
                          initial={{ opacity: 0, x: -30 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.4, delay: idx * 0.03 }}
                          whileHover={{ scale: 1.01, backgroundColor: "rgba(34,197,94,0.08)", transition: { duration: 0.1 } }}
                        >
                          <td className="date-cell"><span>{item.date}</span></td>
                          <td className="activity-cell">{item.activity}</td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </motion.div>
              </motion.div>
            )}

            {activeTab === 'guidelines' && (
              <motion.div
                key="guidelines"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -40 }}
                transition={{ duration: 0.5 }}
                className="guidelines-section"
              >
                <motion.div 
                  className="section-header"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <motion.div 
                    className="section-badge"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.4, type: "spring" }}
                  >
                    <i className="fas fa-gavel"></i> Rules & Regulations
                  </motion.div>
                  <h2>Key <span className="text-green">Guidelines</span></h2>
                  <motion.div className="section-line" />
                  <p>Summary only - the full document is the binding reference.</p>
                </motion.div>

                <motion.div 
                  className="guidelines-grid"
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                >
                  {guidelines.map((guide, idx) => (
                    <motion.div 
                      key={idx} 
                      className="guideline-card"
                      variants={fadeInUp}
                      whileHover="hover"
                      initial="rest"
                      animate="rest"
                    >
                      <motion.div 
                        className="guideline-number"
                        whileHover={{ scale: 1.15, rotate: 5, backgroundColor: "#22c55e", color: "white" }}
                      >
                        {guide.num}
                      </motion.div>
                      <motion.h3>{guide.title}</motion.h3>
                      <div className="guideline-content">
                        {guide.content.map((item, i) => (
                          <motion.p 
                            key={i}
                            initial={{ opacity: 0, x: -15 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3, delay: (idx * 0.05) + (i * 0.02) }}
                          >
                            {item.startsWith('•') ? (
                              <><span className="bullet-point">▹</span> {item.substring(1)}</>
                            ) : item.startsWith('Eight') ? (
                              <><strong>{item}</strong></>
                            ) : !item.startsWith('•') && item.includes(':') ? (
                              <><strong>{item.split(':')[0]}:</strong> {item.split(':')[1]}</>
                            ) : (
                              item
                            )}
                          </motion.p>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                </motion.div>

                <motion.div 
                  className="download-section"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <motion.button 
                      whileHover={{ scale: 1.05, y: -5, boxShadow: "0 15px 35px -10px rgba(34,197,94,0.4)" }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleViewPDF('guidelines')}
                      className="btn btn-primary btn-large"
                    >
                      <i className="fas fa-eye"></i> View Full Guidelines PDF
                      <motion.span className="btn-glow" />
                    </motion.button>
                  </div>
                </motion.div>
              </motion.div>
            )}

            {activeTab === 'committee' && (
              <motion.div
                key="committee"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -40 }}
                transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
                className="committee-section"
              >
                <motion.div 
                  className="section-header"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <motion.div 
                    className="section-badge"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.4, type: "spring" }}
                  >
                    <i className="fas fa-users"></i> Leadership
                  </motion.div>
                  <h2>The Electoral Committee <span className="text-green">(ELCO)</span></h2>
                  <motion.div className="section-line" />
                  <p>Appointed by Senior Leadership under Article IV of the RA Club Constitution to organise, conduct and oversee the 2026 election.</p>
                </motion.div>

                <motion.div 
                  className="chairman-card"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.1, type: "spring", stiffness: 100 }}
                  whileHover={{ scale: 1.02, transition: { duration: 0.3 } }}
                >
                  <motion.div 
                    className="chairman-avatar"
                    initial={{ rotate: -10, scale: 0.9 }}
                    animate={{ rotate: 0, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.2, type: "spring" }}
                    whileHover={{ scale: 1.05, rotate: 5 }}
                  >
                    {elcoMembers.chairman.image ? (
                      <motion.img 
                        src={getImagePath(elcoMembers.chairman.image)} 
                        alt={elcoMembers.chairman.name}
                        className="chairman-image"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.5 }}
                      />
                    ) : (
                      <motion.div 
                        className="avatar-placeholder large"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.5 }}
                      >
                        <span>{elcoMembers.chairman.initials}</span>
                      </motion.div>
                    )}
                  </motion.div>
                  <motion.div 
                    className="chairman-info"
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                  >
                    <motion.span 
                      className="role-badge"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.4 }}
                    >
                      <i className="fas fa-crown"></i> Lead · Chairman
                    </motion.span>
                    <motion.h3
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.5 }}
                    >
                      {elcoMembers.chairman.name}
                    </motion.h3>
                    <motion.p
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.6 }}
                    >
                      {elcoMembers.chairman.description}
                    </motion.p>
                    <motion.a 
                      href={`mailto:${elcoMembers.chairman.email}`} 
                      className="email-link"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.7 }}
                      whileHover={{ x: 5, backgroundColor: "#4ade80", color: "#0a2e1f" }}
                    >
                      <i className="fas fa-envelope"></i> {elcoMembers.chairman.email}
                    </motion.a>
                  </motion.div>
                </motion.div>

                <div className="committee-sub-tabs">
                  {committeeSubTabs.map((tab) => (
                    <motion.button
                      key={tab.id}
                      className={`sub-tab-btn ${committeeSubTab === tab.id ? 'active' : ''}`}
                      onClick={() => setCommitteeSubTab(tab.id)}
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <i className={`fas ${tab.icon}`}></i>
                      <span>{tab.label}</span>
                      {committeeSubTab === tab.id && (
                        <motion.div 
                          className="sub-tab-indicator" 
                          layoutId="subTabIndicator"
                          transition={{ type: "spring", stiffness: 500, damping: 30 }}
                        />
                      )}
                    </motion.button>
                  ))}
                </div>

                <div className="sub-tab-panels">
                  <AnimatePresence mode="wait">
                    {committeeSubTab === 'secretariat' && (
                      <motion.div
                        key="secretariat"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.4 }}
                        className="sub-tab-panel"
                      >
                        <div className="panel-header">
                          <h3><i className="fas fa-building"></i> Secretariat</h3>
                          <p>Managing the administrative backbone of the 2026 election process.</p>
                        </div>
                        <motion.div 
                          className="members-grid"
                          variants={staggerContainer}
                          initial="hidden"
                          animate="visible"
                        >
                          {committeeSubTabs.find(t => t.id === 'secretariat').members.map((member, idx) => (
                            <motion.div 
                              key={idx} 
                              className="member-card"
                              variants={fadeInUp}
                              whileHover="hover"
                              initial="rest"
                              animate="rest"
                            >
                              <motion.div 
                                className="member-avatar"
                                whileHover={{ scale: 1.15, rotate: 5, backgroundColor: "#22c55e" }}
                              >
                                <span>{member.initials}</span>
                              </motion.div>
                              <div className="member-info">
                                <h4>{member.name}</h4>
                                <p>{member.role}</p>
                                <motion.a 
                                  href={`mailto:${member.email}`} 
                                  className="small-email"
                                  whileHover={{ x: 5, color: "#22c55e" }}
                                >
                                  <i className="fas fa-envelope"></i> Contact
                                </motion.a>
                              </div>
                            </motion.div>
                          ))}
                        </motion.div>
                      </motion.div>
                    )}

                    {committeeSubTab === 'committee' && (
                      <motion.div
                        key="committee-members"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.4 }}
                        className="sub-tab-panel"
                      >
                        <div className="panel-header">
                          <h3><i className="fas fa-handshake"></i> Committee Members</h3>
                          <p>Dedicated members ensuring a fair and transparent election process.</p>
                        </div>
                        <motion.div 
                          className="members-grid"
                          variants={staggerContainer}
                          initial="hidden"
                          animate="visible"
                        >
                          {committeeSubTabs.find(t => t.id === 'committee').members.map((member, idx) => (
                            <motion.div 
                              key={idx} 
                              className="member-card"
                              variants={fadeInUp}
                              whileHover="hover"
                              initial="rest"
                              animate="rest"
                            >
                              <motion.div 
                                className="member-avatar"
                                whileHover={{ scale: 1.15, rotate: 5, backgroundColor: "#22c55e" }}
                              >
                                <span>{member.initials}</span>
                              </motion.div>
                              <div className="member-info">
                                <h4>{member.name}</h4>
                                <p>{member.role}</p>
                                <motion.a 
                                  href={`mailto:${member.email}`} 
                                  className="small-email"
                                  whileHover={{ x: 5, color: "#22c55e" }}
                                >
                                  <i className="fas fa-envelope"></i> Contact
                                </motion.a>
                              </div>
                            </motion.div>
                          ))}
                        </motion.div>
                      </motion.div>
                    )}

                    {committeeSubTab === 'publication' && (
                      <motion.div
                        key="publication"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.4 }}
                        className="sub-tab-panel"
                      >
                        <div className="panel-header">
                          <h3><i className="fas fa-newspaper"></i> Publication Team</h3>
                          <p>Responsible for all official communications and announcements.</p>
                        </div>
                        <motion.div 
                          className="members-grid"
                          variants={staggerContainer}
                          initial="hidden"
                          animate="visible"
                        >
                          {committeeSubTabs.find(t => t.id === 'publication').members.map((member, idx) => (
                            <motion.div 
                              key={idx} 
                              className="member-card"
                              variants={fadeInUp}
                              whileHover="hover"
                              initial="rest"
                              animate="rest"
                            >
                              <motion.div 
                                className="member-avatar"
                                whileHover={{ scale: 1.15, rotate: 5, backgroundColor: "#22c55e" }}
                              >
                                <span>{member.initials}</span>
                              </motion.div>
                              <div className="member-info">
                                <h4>{member.name}</h4>
                                <p>{member.role}</p>
                                <motion.a 
                                  href={`mailto:${member.email}`} 
                                  className="small-email"
                                  whileHover={{ x: 5, color: "#22c55e" }}
                                >
                                  <i className="fas fa-envelope"></i> Contact
                                </motion.a>
                              </div>
                            </motion.div>
                          ))}
                        </motion.div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      <motion.section 
        className="contact-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8 }}
      >
        <div className="container">
          <motion.div 
            className="section-header center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.div 
              className="section-badge"
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, type: "spring" }}
            >
              <i className="fas fa-envelope"></i> Get in Touch
            </motion.div>
            <h2>Contact the <span className="text-green">Electoral Committee</span></h2>
            <motion.div className="section-line" />
            <p>Questions on eligibility, the nomination form, or any part of the process? Reach out directly.</p>
          </motion.div>

          <motion.div 
            className="contact-grid"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {[
              { name: 'Samson Adeosun', role: 'ELCO Chairman', email: 'samson.adeosun@nlng.com', initials: 'SA', image: 'SA_RA Club.jpg' },
              { name: 'Abdulmalik Ibrahim', role: 'ELCO Secretary', email: 'abdulmalik.ibrahim@nlng.com', initials: 'AI', image: null },
              { name: 'Emmanuel Nwachukwu', role: 'ELCO Member', email: 'emmanuel.nwachukwu@nlng.com', initials: 'EN', image: null }
            ].map((contact, idx) => (
              <motion.div 
                key={idx}
                className="contact-card"
                variants={fadeInUp}
                whileHover="hover"
                initial="rest"
                animate="rest"
              >
                <motion.div 
                  className="contact-avatar"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  {contact.image ? (
                    <img 
                      src={getImagePath(contact.image)} 
                      alt={contact.name}
                      className="contact-image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = `<span>${contact.initials}</span>`;
                      }}
                    />
                  ) : (
                    <span>{contact.initials}</span>
                  )}
                </motion.div>
                <motion.h4>{contact.name}</motion.h4>
                <p className="contact-role">{contact.role}</p>
                <motion.a 
                  href={`mailto:${contact.email}`} 
                  className="contact-email"
                  whileHover={{ x: 8, backgroundColor: "#22c55e", color: "white" }}
                >
                  <i className="fas fa-envelope"></i> {contact.email}
                </motion.a>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* Candidates Modal */}
      <AnimatePresence>
        {showCandidatesModal && (
          <motion.div 
            className="candidates-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowCandidatesModal(false)}
          >
            <motion.div 
              className="candidates-modal-container"
              initial={{ scale: 0.9, y: 50, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 50, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="candidates-modal-header">
                <div className="candidates-modal-icon">
                  <i className="fas fa-users-viewfinder"></i>
                </div>
                <div className="candidates-modal-title-section">
                  <h2>Candidates List</h2>
                  <p>ELCO 2026 Elections</p>
                </div>
                <button className="candidates-modal-close" onClick={() => setShowCandidatesModal(false)}>
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="candidates-modal-body">
                <div className="candidates-table-container">
                  <table className="candidates-modal-table">
                    <thead>
                      <tr>
                        <th>S/N</th>
                        <th>Position</th>
                        <th>Candidate(s)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {candidatesData.map((item, idx) => (
                        <motion.tr 
                          key={idx}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.05 }}
                          className={item.candidates === 'No submission received' ? 'no-submission-row' : ''}
                        >
                          <td className="serial-cell">{idx + 1}</td>
                          <td className="position-cell-modal">{item.position}</td>
                          <td className="candidates-cell-modal">
                            {item.candidates === 'No submission received' ? (
                              <span className="no-submission-badge">
                                <i className="fas fa-clock"></i> No submission received
                              </span>
                            ) : (
                              <div className="candidates-list">
                                {item.candidates.split(';').map((cand, i) => (
                                  <span key={i} className="candidate-tag">
                                    <i className="fas fa-user-check"></i>
                                    {cand.trim()}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="candidates-modal-footer">
                <motion.button 
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="candidates-modal-btn"
                  onClick={() => setShowCandidatesModal(false)}
                >
                  <i className="fas fa-check-circle"></i> Got it
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style jsx>{`
        .election-page {
          background: #ffffff;
          overflow-x: hidden;
          position: relative;
        }

        .bg-animation {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          z-index: 0;
          overflow: hidden;
        }

        .gradient-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.4;
          animation: float 20s infinite ease-in-out;
        }

        .orb-1 {
          width: 400px;
          height: 400px;
          background: rgba(34, 197, 94, 0.15);
          top: -100px;
          right: -100px;
        }
        .orb-2 {
          width: 300px;
          height: 300px;
          background: rgba(22, 101, 52, 0.1);
          bottom: 20%;
          left: -80px;
          animation-delay: -5s;
        }
        .orb-3 {
          width: 500px;
          height: 500px;
          background: rgba(34, 197, 94, 0.08);
          bottom: -150px;
          right: 20%;
          animation-delay: -10s;
        }

        @keyframes float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -30px) scale(1.05); }
          66% { transform: translate(-20px, 20px) scale(0.95); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.3); }
        }

        .container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 24px;
          position: relative;
          z-index: 2;
        }

        @media (max-width: 768px) {
          .container { padding: 0 20px; }
        }

        @media (max-width: 480px) {
          .container { padding: 0 16px; }
        }

        .election-hero {
          position: relative;
          background: linear-gradient(135deg, #0a2e1f 0%, #0f3d24 50%, #0a2e1f 100%);
          padding: 140px 0 100px;
          color: white;
          overflow: hidden;
        }

        @media (max-width: 768px) {
          .election-hero { padding: 100px 0 70px; }
        }

        @media (max-width: 480px) {
          .election-hero { padding: 80px 0 60px; }
        }

        .hero-overlay {
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse at 30% 40%, rgba(34, 197, 94, 0.15), transparent 70%);
          pointer-events: none;
        }

        .hero-wave {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 80px;
          background: linear-gradient(135deg, transparent 50%, white 50%);
          transform-origin: left;
        }

        @media (max-width: 768px) {
          .hero-wave { height: 50px; }
        }

        .hero-content {
          position: relative;
          z-index: 2;
          max-width: 800px;
        }

        .hero-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 6px 16px;
          background: rgba(34, 197, 94, 0.15);
          border: 1px solid rgba(34, 197, 94, 0.3);
          border-radius: 999px;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #bbf7d0;
          margin-bottom: 24px;
        }

        .pulse-dot {
          width: 8px;
          height: 8px;
          background: #4ade80;
          border-radius: 50%;
          display: inline-block;
          animation: pulse 1.5s infinite;
        }

        .hero-title {
          font-size: 58px;
          font-weight: 800;
          margin: 0 0 24px;
          line-height: 1.15;
        }

        @media (max-width: 768px) {
          .hero-title { font-size: 40px; }
        }

        @media (max-width: 480px) {
          .hero-title { font-size: 32px; }
        }

        .text-gradient {
          background: linear-gradient(135deg, #4ade80, #22c55e, #166534);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }

        .hero-subtitle {
          font-size: 18px;
          color: rgba(255,255,255,0.85);
          margin-bottom: 40px;
          line-height: 1.6;
        }

        @media (max-width: 768px) {
          .hero-subtitle { font-size: 16px; }
        }

        .hero-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          align-items: center;
        }

        .btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 12px 24px;
          border-radius: 40px;
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border: none;
          position: relative;
          overflow: hidden;
          z-index: 1;
          white-space: nowrap;
          flex-shrink: 0;
        }

        @media (max-width: 900px) {
          .btn {
            padding: 10px 18px;
            font-size: 13px;
            gap: 8px;
          }
        }

        @media (max-width: 768px) {
          .hero-actions {
            gap: 12px;
          }
          .btn {
            padding: 10px 16px;
            font-size: 12px;
            gap: 6px;
            white-space: nowrap;
            flex-shrink: 0;
          }
        }

        @media (max-width: 640px) {
          .hero-actions {
            gap: 10px;
          }
          .btn {
            padding: 8px 14px;
            font-size: 11px;
            gap: 5px;
          }
        }

        @media (max-width: 550px) {
          .hero-actions {
            gap: 8px;
          }
          .btn {
            padding: 7px 12px;
            font-size: 10px;
            gap: 4px;
          }
          .btn i {
            font-size: 10px;
          }
        }

        .btn-primary {
          background: transparent;
          border: 1.5px solid #22c55e;
          color: #4ade80;
        }

        .btn-primary .btn-glow {
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
          transition: left 0.5s;
          z-index: -1;
        }

        .btn-primary:hover .btn-glow { left: 100%; }
        .btn-primary:hover {
          background: linear-gradient(135deg, #22c55e, #166534);
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 10px 25px -5px rgba(34, 197, 94, 0.4);
          border-color: transparent;
        }

        .btn-outline {
          background: transparent;
          border: 1.5px solid rgba(255, 255, 255, 0.3);
          color: rgba(255, 255, 255, 0.9);
        }
        .btn-outline:hover {
          border-color: #22c55e;
          background: #22c55e;
          color: white;
          transform: translateY(-2px);
        }

        .btn-secondary {
          background: rgba(34, 197, 94, 0.15);
          border: 1.5px solid #22c55e;
          color: #4ade80;
        }
        .btn-secondary:hover {
          background: #22c55e;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 8px 20px -8px rgba(34, 197, 94, 0.4);
        }

        .btn-yellow {
          background: #eab308;
          color: #1e293b;
          border: none;
          box-shadow: 0 4px 14px rgba(234, 179, 8, 0.3);
        }
        .btn-yellow:hover {
          background: #ca8a04;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 10px 25px -5px rgba(234, 179, 8, 0.5);
        }

        .btn-large { padding: 16px 40px; font-size: 16px; }
        @media (max-width: 768px) { .btn-large { padding: 14px 24px; font-size: 14px; } }

        .info-strip {
          background: #ffffff;
          border-bottom: 1px solid #e5e9e6;
          padding: 40px 0;
        }

        @media (max-width: 768px) { .info-strip { padding: 30px 0; } }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 32px;
        }

        @media (max-width: 768px) {
          .info-grid { grid-template-columns: repeat(2, 1fr); gap: 16px; }
        }
        @media (max-width: 480px) { .info-grid { grid-template-columns: 1fr; } }

        .info-tile {
          display: flex;
          align-items: center;
          gap: 18px;
          padding: 16px 24px;
          background: #f8faf8;
          border-radius: 20px;
          cursor: pointer;
          transition: all 0.3s ease;
          border: 1px solid transparent;
        }

        @media (max-width: 768px) { .info-tile { padding: 12px 16px; gap: 14px; } }

        .info-tile:hover {
          border-color: #22c55e;
          background: white;
        }

        .info-icon {
          width: 52px;
          height: 52px;
          background: linear-gradient(135deg, #f0fdf4, #dcfce7);
          border-radius: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #166534;
          font-size: 24px;
        }

        @media (max-width: 768px) { .info-icon { width: 44px; height: 44px; font-size: 20px; } }

        .info-label {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #9ca3af;
          display: block;
          margin-bottom: 4px;
        }

        .info-value {
          font-size: 18px;
          font-weight: 800;
          color: #111827;
        }

        @media (max-width: 768px) { .info-value { font-size: 14px; } }

        .election-tabs {
          background: white;
          border-bottom: 1px solid #e5e9e6;
          position: sticky;
          top: 0;
          z-index: 100;
          background: rgba(255,255,255,0.98);
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
        }

        .election-tabs.scrolled { box-shadow: 0 4px 20px rgba(0,0,0,0.08); }

        .tabs-nav {
          display: flex;
          gap: 12px;
          padding: 20px 0;
        }

        @media (max-width: 768px) {
          .tabs-nav {
            gap: 8px;
            padding: 12px 0;
            overflow-x: auto;
            flex-wrap: nowrap;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: thin;
          }
          .tabs-nav::-webkit-scrollbar { height: 3px; }
          .tabs-nav::-webkit-scrollbar-track { background: #f0fdf4; border-radius: 10px; }
          .tabs-nav::-webkit-scrollbar-thumb { background: #22c55e; border-radius: 10px; }
        }

        .tab-btn {
          padding: 10px 28px;
          background: transparent;
          border: none;
          font-size: 14px;
          font-weight: 600;
          color: #666666;
          cursor: pointer;
          border-radius: 40px;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 10px;
          position: relative;
          white-space: nowrap;
        }

        @media (max-width: 768px) {
          .tab-btn {
            padding: 8px 20px;
            font-size: 13px;
            flex: 0 0 auto;
          }
          .tab-btn i { font-size: 13px; }
        }

        @media (max-width: 480px) {
          .tab-btn { padding: 8px 16px; font-size: 12px; gap: 6px; }
          .tab-btn i { font-size: 12px; }
        }

        .tab-btn:hover {
          color: #166534;
          background: #f0fdf4;
        }

        .tab-btn.active {
          background: #166534;
          color: white;
        }

        .tab-indicator {
          position: absolute;
          bottom: -20px;
          left: 0;
          right: 0;
          height: 3px;
          background: #22c55e;
          border-radius: 3px;
        }

        @media (max-width: 768px) {
          .tab-indicator { bottom: -12px; height: 2px; }
        }

        .election-content { padding: 80px 0; background: white; }
        @media (max-width: 768px) { .election-content { padding: 50px 0; } }

        .section-header { text-align: center; margin-bottom: 64px; }
        @media (max-width: 768px) { .section-header { margin-bottom: 40px; } }

        .section-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 16px;
          background: #f0fdf4;
          border-radius: 40px;
          font-size: 12px;
          font-weight: 600;
          color: #166534;
          margin-bottom: 20px;
        }

        .section-header h2 {
          font-size: 42px;
          font-weight: 800;
          margin-bottom: 20px;
          color: #111827;
        }

        @media (max-width: 768px) { .section-header h2 { font-size: 32px; } }
        @media (max-width: 480px) { .section-header h2 { font-size: 28px; } }

        .section-header .text-green { color: #166534; }

        .section-line {
          width: 60px;
          height: 4px;
          background: linear-gradient(90deg, #22c55e, #166534);
          border-radius: 4px;
          margin: 20px auto;
        }

        .section-header p {
          font-size: 16px;
          color: #666666;
          max-width: 680px;
          margin: 0 auto;
          line-height: 1.6;
        }

        .chairman-card {
          background: linear-gradient(135deg, #0a2e1f, #14532d);
          border-radius: 32px;
          padding: 52px;
          display: grid;
          grid-template-columns: 200px 1fr;
          gap: 48px;
          align-items: center;
          margin-bottom: 64px;
          position: relative;
          overflow: hidden;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }

        @media (max-width: 768px) {
          .chairman-card {
            grid-template-columns: 1fr;
            text-align: center;
            padding: 36px;
            gap: 28px;
          }
        }

        .chairman-image {
          width: 200px;
          height: 200px;
          border-radius: 50%;
          object-fit: cover;
          border: 4px solid #4ade80;
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
        }

        @media (max-width: 768px) {
          .chairman-image, .avatar-placeholder { width: 160px; height: 160px; margin: 0 auto; }
          .avatar-placeholder.large span { font-size: 56px; }
        }

        .avatar-placeholder {
          width: 200px;
          height: 200px;
          border-radius: 50%;
          background: linear-gradient(135deg, #4ade80, #166534);
          display: flex;
          align-items: center;
          justify-content: center;
          border: 4px solid #4ade80;
        }
        .avatar-placeholder.large span { font-size: 72px; font-weight: 800; color: white; }

        .chairman-info { position: relative; z-index: 1; color: white; }
        .role-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 16px;
          background: rgba(74, 222, 128, 0.2);
          border: 1px solid rgba(74, 222, 128, 0.5);
          border-radius: 40px;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          color: #bbf7d0;
          margin-bottom: 24px;
        }
        .chairman-info h3 { font-size: 38px; margin: 0 0 16px; font-weight: 700; color: white; }
        @media (max-width: 768px) { .chairman-info h3 { font-size: 28px; } }
        .chairman-info p { color: rgba(255, 255, 255, 0.9); margin-bottom: 28px; line-height: 1.6; font-size: 16px; }
        .email-link {
          color: #bbf7d0;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 12px;
          font-size: 14px;
          padding: 10px 24px;
          background: rgba(255,255,255,0.1);
          border-radius: 40px;
          transition: all 0.2s;
        }

        .committee-sub-tabs {
          display: flex;
          gap: 16px;
          margin-bottom: 40px;
          border-bottom: 2px solid #eef2f0;
          padding-bottom: 0;
        }

        @media (max-width: 768px) {
          .committee-sub-tabs {
            gap: 10px;
            overflow-x: auto;
            flex-wrap: nowrap;
            -webkit-overflow-scrolling: touch;
            padding-bottom: 4px;
          }
        }

        .sub-tab-btn {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 28px;
          background: transparent;
          border: none;
          font-size: 15px;
          font-weight: 600;
          color: #666666;
          cursor: pointer;
          border-radius: 40px 40px 0 0;
          transition: all 0.3s ease;
          position: relative;
          white-space: nowrap;
        }

        @media (max-width: 768px) {
          .sub-tab-btn {
            padding: 8px 18px;
            font-size: 13px;
            gap: 8px;
          }
          .sub-tab-btn i { font-size: 13px; }
        }

        @media (max-width: 480px) {
          .sub-tab-btn { padding: 8px 14px; font-size: 12px; gap: 6px; }
          .sub-tab-btn i { font-size: 11px; }
        }

        .sub-tab-btn:hover { color: #166534; background: #f0fdf4; }
        .sub-tab-btn.active { color: #166534; background: linear-gradient(180deg, #f0fdf4 0%, white 100%); }
        .sub-tab-indicator {
          position: absolute;
          bottom: -2px;
          left: 0;
          right: 0;
          height: 3px;
          background: #22c55e;
          border-radius: 3px;
        }

        @media (max-width: 480px) { .sub-tab-indicator { height: 2px; } }

        .panel-header { margin-bottom: 32px; }
        .panel-header h3 {
          font-size: 28px;
          font-weight: 700;
          color: #111827;
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        @media (max-width: 768px) { .panel-header h3 { font-size: 24px; } }
        .panel-header p { font-size: 15px; color: #666666; line-height: 1.6; }

        .members-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 28px;
        }
        @media (max-width: 768px) { .members-grid { gap: 20px; } }
        @media (max-width: 640px) { .members-grid { grid-template-columns: 1fr; } }

        .member-card {
          background: white;
          border: 1px solid #eef2f0;
          border-radius: 24px;
          padding: 24px;
          display: flex;
          align-items: center;
          gap: 20px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          min-height: 130px;
        }
        @media (max-width: 480px) { .member-card { padding: 18px; gap: 15px; } }
        .member-card:hover { border-color: #22c55e; box-shadow: 0 20px 35px -12px rgba(15, 46, 31, 0.15); }
        .member-avatar {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: linear-gradient(135deg, #166534, #22c55e);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        @media (max-width: 480px) { .member-avatar { width: 52px; height: 52px; } }
        .member-avatar span { font-size: 24px; font-weight: 700; color: white; }
        @media (max-width: 480px) { .member-avatar span { font-size: 18px; } }
        .member-info h4 { font-size: 18px; font-weight: 700; margin: 0 0 6px; color: #111827; }
        @media (max-width: 480px) { .member-info h4 { font-size: 16px; } }
        .member-info p { font-size: 13px; color: #666666; margin: 0 0 10px; }
        .small-email {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: #166534;
          font-size: 12px;
          text-decoration: none;
          font-weight: 500;
        }

        .timetable-wrapper {
          background: white;
          border: 1px solid #eef2f0;
          border-radius: 24px;
          overflow-x: auto;
        }
        .timetable { width: 100%; border-collapse: collapse; min-width: 600px; }
        .timetable th {
          background: linear-gradient(135deg, #0a2e1f, #14532d);
          color: white;
          padding: 16px 24px;
          text-align: left;
          font-weight: 600;
          font-size: 13px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }
        .timetable td { padding: 14px 24px; border-top: 1px solid #f0f2f0; color: #4a5568; }
        @media (max-width: 768px) { .timetable th, .timetable td { padding: 12px 16px; } }
        .highlight-row td { background: rgba(34, 197, 94, 0.06); border-left: 3px solid #22c55e; }
        .date-cell { font-weight: 700; color: #166534; white-space: nowrap; }

        .guidelines-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 32px;
        }
        @media (max-width: 900px) { .guidelines-grid { grid-template-columns: 1fr; gap: 24px; } }
        .guideline-card {
          background: white;
          border: 1px solid #eef2f0;
          border-radius: 28px;
          padding: 32px;
          transition: all 0.3s;
        }
        @media (max-width: 768px) { .guideline-card { padding: 24px; } }
        .guideline-card:hover { border-color: #22c55e; box-shadow: 0 20px 35px -12px rgba(15, 46, 31, 0.12); }
        .guideline-number {
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, #f0fdf4, #dcfce7);
          border-radius: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 22px;
          color: #166534;
          margin-bottom: 24px;
        }
        .guideline-card h3 { font-size: 20px; font-weight: 700; margin: 0 0 20px; color: #111827; }
        .guideline-content p { font-size: 14px; color: #4a5563; line-height: 1.6; margin: 10px 0; }
        .bullet-point { color: #22c55e; font-weight: 700; margin-right: 10px; display: inline-block; }
        .download-section { text-align: center; margin-top: 60px; }

        .contact-section {
          background: linear-gradient(180deg, #f8faf8 0%, #ffffff 100%);
          padding: 80px 0;
        }
        @media (max-width: 768px) { .contact-section { padding: 60px 0; } }
        .contact-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 32px;
          margin-top: 48px;
        }
        @media (max-width: 768px) { .contact-grid { grid-template-columns: 1fr; gap: 24px; } }
        .contact-card {
          background: white;
          border-radius: 28px;
          padding: 36px 24px;
          text-align: center;
          border: 1px solid #eef2f0;
          transition: all 0.3s;
        }
        .contact-card:hover { border-color: #22c55e; box-shadow: 0 25px 45px -15px rgba(15, 46, 31, 0.15); transform: translateY(-3px); }
        .contact-avatar {
          width: 100px;
          height: 100px;
          border-radius: 50%;
          background: linear-gradient(135deg, #166534, #22c55e);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
        }
        @media (max-width: 480px) { .contact-avatar { width: 80px; height: 80px; } }
        .contact-avatar span { font-size: 36px; font-weight: 800; color: white; }
        @media (max-width: 480px) { .contact-avatar span { font-size: 28px; } }
        .contact-card h4 { font-size: 20px; font-weight: 700; margin: 0 0 6px; color: #111827; }
        .contact-role { font-size: 12px; font-weight: 600; color: #22c55e; letter-spacing: 0.06em; text-transform: uppercase; margin-bottom: 20px; }
        .contact-email {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          background: #f8faf8;
          border-radius: 60px;
          font-size: 12px;
          color: #374151;
          text-decoration: none;
          transition: all 0.2s;
        }
        .contact-email:hover { background: #22c55e; color: white; }

        /* ========== PREMIUM FLIP POPUP STYLES ========== */
        .flip-popup-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          backdrop-filter: blur(12px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
          padding: 30px;
        }

        .flip-popup-container {
          background: transparent;
          max-width: 820px;
          width: 100%;
          position: relative;
        }

        .flip-popup-close {
          position: absolute;
          top: -18px;
          right: -18px;
          width: 48px;
          height: 48px;
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(10px);
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          font-size: 20px;
          color: white;
          cursor: pointer;
          transition: all 0.3s ease;
          z-index: 10;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .flip-popup-close:hover {
          background: #eab308;
          border-color: #eab308;
          color: #1e293b;
        }

        .flip-popup-hint {
          text-align: center;
          margin-top: 20px;
          color: rgba(255, 255, 255, 0.6);
          font-size: 13px;
          letter-spacing: 0.5px;
        }

        .flip-popup-hint span {
          background: rgba(255, 255, 255, 0.08);
          padding: 8px 24px;
          border-radius: 30px;
          backdrop-filter: blur(5px);
          border: 1px solid rgba(255, 255, 255, 0.05);
        }

        /* FLIP CARD - Premium Design */
        .flip-card {
          background: transparent;
          width: 100%;
          height: 580px;
          perspective: 1200px;
          cursor: pointer;
        }

        .flip-card-inner {
          position: relative;
          width: 100%;
          height: 100%;
          transform-style: preserve-3d;
        }

        .flip-card-front, .flip-card-back {
          position: absolute;
          width: 100%;
          height: 100%;
          backface-visibility: hidden;
          border-radius: 24px;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* FRONT - Premium Green Card */
        .flip-card-front {
          background: linear-gradient(160deg, #064e3b 0%, #065f46 30%, #047857 60%, #065f46 100%);
          box-shadow: 0 40px 80px -20px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(74, 222, 128, 0.2);
        }

        .flip-front-content {
          text-align: center;
          color: white;
          padding: 40px;
          max-width: 90%;
        }

        .flip-icon {
          width: 90px;
          height: 90px;
          background: rgba(74, 222, 128, 0.15);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 28px;
          border: 2px solid rgba(74, 222, 128, 0.3);
          box-shadow: 0 0 40px rgba(74, 222, 128, 0.1);
        }

        .flip-icon i {
          font-size: 44px;
          color: #4ade80;
        }

        .flip-front-content h2 {
          font-size: 26px;
          font-weight: 600;
          letter-spacing: 3px;
          margin: 0;
          opacity: 0.9;
          text-transform: uppercase;
          color: #ffffff;
        }

        .flip-front-content h1 {
          font-size: 80px;
          font-weight: 900;
          margin: 8px 0 16px;
          background: linear-gradient(135deg, #fcd34d, #f59e0b);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          letter-spacing: 6px;
          text-shadow: none;
        }

        .flip-sub {
          font-size: 18px;
          opacity: 0.85;
          margin: 0 0 32px;
          letter-spacing: 1px;
          color: #d1fae5;
        }

        .flip-tap-hint {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 10px 28px;
          background: rgba(255, 255, 255, 0.08);
          border-radius: 40px;
          font-size: 14px;
          color: rgba(255, 255, 255, 0.8);
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: all 0.3s ease;
        }

        .flip-tap-hint i {
          animation: float-hand 2s ease-in-out infinite;
        }

        @keyframes float-hand {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(6px); }
        }

        /* BACK - Professional Results with Yellowish-Orange Theme & Scroll */
        .flip-card-back {
          background: linear-gradient(165deg, #fffbeb 0%, #fef3c7 25%, #fde68a 60%, #fcd34d 100%);
          transform: rotateY(180deg);
          box-shadow: 0 40px 80px -20px rgba(234, 179, 8, 0.25);
          border: 1px solid rgba(234, 179, 8, 0.2);
          padding: 24px 28px;
          display: block;
          overflow: hidden;
        }

        .flip-back-content {
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          gap: 8px;
          overflow-y: auto;
          padding-right: 4px;
        }

        .flip-back-content::-webkit-scrollbar {
          width: 5px;
        }

        .flip-back-content::-webkit-scrollbar-track {
          background: rgba(234, 179, 8, 0.1);
          border-radius: 10px;
        }

        .flip-back-content::-webkit-scrollbar-thumb {
          background: #eab308;
          border-radius: 10px;
        }

        .flip-back-header {
          text-align: center;
          flex-shrink: 0;
        }

        .flip-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #0a2e1f;
          color: #fcd34d;
          padding: 4px 18px;
          border-radius: 40px;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.5px;
          margin-bottom: 6px;
        }

        .flip-badge i {
          font-size: 11px;
        }

        .flip-back-header h3 {
          font-size: 22px;
          font-weight: 800;
          color: #1e293b;
          margin: 0 0 2px;
        }

        .flip-back-sub {
          font-size: 13px;
          font-weight: 600;
          color: #92400e;
          margin: 0 0 4px;
        }

        .flip-back-note {
          font-size: 11px;
          color: #78350f;
          max-width: 90%;
          margin: 0 auto;
          line-height: 1.4;
          opacity: 0.85;
        }

        .flip-results-table {
          flex: 1;
          overflow: visible;
          border-radius: 14px;
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(5px);
          border: 1px solid rgba(234, 179, 8, 0.15);
          min-height: 200px;
        }

        .flip-results-table table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }

        .flip-results-table thead th {
          background: #0a2e1f;
          color: white;
          padding: 10px 16px;
          text-align: left;
          font-weight: 700;
          font-size: 11px;
          letter-spacing: 0.8px;
          text-transform: uppercase;
          position: sticky;
          top: 0;
          z-index: 2;
        }

        .flip-results-table tbody td {
          padding: 8px 16px;
          border-bottom: 1px solid rgba(234, 179, 8, 0.1);
          color: #1e293b;
        }

        .flip-results-table tbody tr:last-child td {
          border-bottom: none;
        }

        .flip-results-table tbody tr:hover td {
          background: rgba(234, 179, 8, 0.06);
        }

        .pos-cell {
          font-weight: 600;
          color: #0a2e1f;
          width: 42%;
          font-size: 12px;
        }

        .elected-cell {
          width: 58%;
        }

        .elected-name {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: #065f46;
          font-weight: 600;
          font-size: 13px;
        }

        .elected-name i {
          color: #22c55e;
          font-size: 14px;
        }

        .no-sub-row td {
          background: rgba(239, 68, 68, 0.04);
        }

        .no-sub-text {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: #b45309;
          font-style: italic;
          font-weight: 500;
          font-size: 12px;
        }

        .no-sub-text i {
          color: #f59e0b;
        }

        .flip-back-footer {
          flex-shrink: 0;
          padding-top: 8px;
          border-top: 1px solid rgba(234, 179, 8, 0.15);
          text-align: center;
        }

        .flip-back-footer p {
          font-size: 11px;
          color: #78350f;
          margin: 3px 0;
          line-height: 1.4;
        }

        .flip-back-footer .flip-issued {
          font-size: 9px;
          opacity: 0.7;
          letter-spacing: 0.3px;
        }

        .flip-back-footer i {
          color: #eab308;
          margin-right: 6px;
        }

        /* ========== CANDIDATES MODAL STYLES ========== */
        .candidates-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .candidates-modal-container {
          background: white;
          border-radius: 32px;
          max-width: 900px;
          width: 100%;
          max-height: 85vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          box-shadow: 0 25px 60px -15px rgba(0, 0, 0, 0.4);
        }

        .candidates-modal-header {
          background: linear-gradient(135deg, #fefce8 0%, #fef9c3 100%);
          padding: 24px 32px;
          display: flex;
          align-items: center;
          gap: 18px;
          border-bottom: 2px solid #eab308;
        }

        .candidates-modal-icon {
          width: 56px;
          height: 56px;
          background: linear-gradient(135deg, #eab308, #ca8a04);
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 20px -5px rgba(234, 179, 8, 0.4);
        }

        .candidates-modal-icon i {
          font-size: 28px;
          color: white;
        }

        .candidates-modal-title-section {
          flex: 1;
        }

        .candidates-modal-title-section h2 {
          font-size: 24px;
          font-weight: 800;
          margin: 0 0 4px 0;
          color: #1e293b;
        }

        .candidates-modal-title-section p {
          font-size: 13px;
          color: #666;
          margin: 0;
        }

        .candidates-modal-close {
          width: 40px;
          height: 40px;
          background: rgba(0, 0, 0, 0.05);
          border: none;
          border-radius: 50%;
          font-size: 18px;
          cursor: pointer;
          color: #666;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .candidates-modal-close:hover {
          background: #eab308;
          color: white;
          transform: rotate(90deg);
        }

        .candidates-modal-body {
          padding: 28px 32px;
          overflow-y: auto;
          flex: 1;
          background: #fafbfc;
        }

        .candidates-table-container {
          overflow-x: auto;
          border-radius: 20px;
          background: white;
          border: 1px solid #eef2f0;
        }

        .candidates-modal-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        }

        .candidates-modal-table th {
          background: #0a2e1f;
          color: white;
          padding: 16px 20px;
          text-align: left;
          font-weight: 700;
          font-size: 13px;
          letter-spacing: 0.5px;
        }

        .candidates-modal-table td {
          padding: 16px 20px;
          border-bottom: 1px solid #f0f2f0;
          vertical-align: middle;
        }

        .candidates-modal-table tr:last-child td {
          border-bottom: none;
        }

        .candidates-modal-table tr:hover td {
          background: #fefce8;
        }

        .serial-cell {
          width: 60px;
          font-weight: 700;
          color: #eab308;
          font-size: 16px;
        }

        .position-cell-modal {
          font-weight: 700;
          color: #166534;
          background: #f8faf8;
          width: 35%;
        }

        .candidates-cell-modal {
          color: #334155;
        }

        .candidates-list {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .candidate-tag {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: linear-gradient(135deg, #f0fdf4, #dcfce7);
          padding: 6px 14px;
          border-radius: 30px;
          font-size: 13px;
          font-weight: 500;
          color: #166534;
        }

        .candidate-tag i {
          font-size: 11px;
          color: #22c55e;
        }

        .no-submission-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #fefce8;
          padding: 6px 14px;
          border-radius: 30px;
          font-size: 13px;
          color: #ca8a04;
          border: 1px solid #fde047;
        }

        .no-submission-badge i {
          font-size: 12px;
        }

        .no-submission-row td {
          background: #fefce8;
        }

        .candidates-modal-footer {
          padding: 20px 32px;
          border-top: 1px solid #eef2f0;
          display: flex;
          justify-content: flex-end;
          background: white;
        }

        .candidates-modal-btn {
          padding: 12px 32px;
          background: linear-gradient(135deg, #eab308, #ca8a04);
          border: none;
          border-radius: 40px;
          font-weight: 700;
          font-size: 14px;
          cursor: pointer;
          color: white;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .candidates-modal-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px -8px rgba(234, 179, 8, 0.5);
        }

        @media (max-width: 768px) {
          .flip-card {
            height: 520px;
          }
          .flip-front-content h1 {
            font-size: 56px;
          }
          .flip-front-content h2 {
            font-size: 20px;
          }
          .flip-icon {
            width: 70px;
            height: 70px;
          }
          .flip-icon i {
            font-size: 34px;
          }
          .flip-card-back {
            padding: 18px 20px;
          }
          .flip-back-header h3 {
            font-size: 20px;
          }
          .flip-back-note {
            font-size: 11px;
            max-width: 100%;
          }
          .flip-results-table table {
            font-size: 12px;
          }
          .flip-results-table thead th,
          .flip-results-table tbody td {
            padding: 8px 14px;
          }
          .pos-cell {
            font-size: 11px;
          }
          .elected-name {
            font-size: 12px;
          }
          .flip-popup-close {
            top: -12px;
            right: -12px;
            width: 40px;
            height: 40px;
            font-size: 17px;
          }
          .flip-popup-hint span {
            font-size: 12px;
            padding: 6px 18px;
          }

          .candidates-modal-header {
            padding: 18px 22px;
            flex-wrap: wrap;
          }
          .candidates-modal-icon {
            width: 44px;
            height: 44px;
          }
          .candidates-modal-icon i {
            font-size: 22px;
          }
          .candidates-modal-title-section h2 {
            font-size: 18px;
          }
          .candidates-modal-body {
            padding: 20px;
          }
          .candidates-modal-footer {
            padding: 16px 20px;
          }
          .candidate-tag {
            font-size: 11px;
            padding: 4px 10px;
          }
          .candidates-modal-table th,
          .candidates-modal-table td {
            padding: 12px 14px;
            font-size: 12px;
          }
          .serial-cell {
            width: 45px;
          }
        }

        @media (max-width: 480px) {
          .flip-card {
            height: 480px;
          }
          .flip-front-content h1 {
            font-size: 42px;
          }
          .flip-front-content h2 {
            font-size: 16px;
          }
          .flip-icon {
            width: 60px;
            height: 60px;
          }
          .flip-icon i {
            font-size: 28px;
          }
          .flip-sub {
            font-size: 14px;
          }
          .flip-tap-hint {
            font-size: 12px;
            padding: 8px 20px;
          }
          .flip-card-back {
            padding: 14px 16px;
          }
          .flip-back-header h3 {
            font-size: 17px;
          }
          .flip-back-sub {
            font-size: 12px;
          }
          .flip-back-note {
            font-size: 10px;
          }
          .flip-results-table table {
            font-size: 10px;
          }
          .flip-results-table thead th,
          .flip-results-table tbody td {
            padding: 6px 10px;
          }
          .pos-cell {
            font-size: 10px;
          }
          .elected-name {
            font-size: 10px;
          }
          .no-sub-text {
            font-size: 10px;
          }
          .flip-back-footer p {
            font-size: 9px;
          }
          .flip-back-footer .flip-issued {
            font-size: 8px;
          }

          .candidates-modal-container {
            max-height: 90vh;
          }
          .candidates-modal-header {
            padding: 14px 18px;
          }
          .candidates-modal-title-section h2 {
            font-size: 16px;
          }
          .candidates-modal-title-section p {
            font-size: 10px;
          }
          .candidate-tag {
            font-size: 10px;
            padding: 3px 8px;
          }
          .flip-popup-overlay {
            padding: 16px;
          }
          .flip-popup-close {
            top: -8px;
            right: -8px;
            width: 34px;
            height: 34px;
            font-size: 14px;
          }
          .flip-popup-hint span {
            font-size: 10px;
            padding: 5px 14px;
          }
        }
      `}</style>
    </div>
  );
};

export default Election2026;